import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Progress,
  Select,
  Stack,
  SimpleGrid,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleCheck,
  IconX,
} from "@tabler/icons-react";
import type {
  DocDetail,
  DocumentRejectionReason,
  Driver,
  DriverDocumentStatus,
  DriverDocumentType,
  RideType,
} from "../../types/domain";
import { ImageThumb } from "../../components/common/ImageThumb";
import { en, parseBackendDate } from "../../lib/format";
import { humanize } from "../../lib/labels";
import { fromDdMmYyyy, toDdMmYyyy } from "./licenseExpiry";
import {
  licenseError,
  normalizeLicense,
  normalizePlate,
  plateError,
} from "./latraFormats";

/** Fields LATRA/CeTS needs, confirmed by the admin at approval. */
export interface LatraApprovalDetails {
  registrationNumber: string;
  licenseNumber: string;
}

const DOC_STATUS_COLORS: Record<DriverDocumentStatus, string> = {
  pending: "gray",
  underReview: "blue",
  approved: "green",
  rejected: "red",
};

const REJECTION_LABELS: Record<DocumentRejectionReason, string> = {
  invalidDocument: "Invalid document",
  wrongSlot: "Uploaded on the wrong slot",
  expiredDocument: "Expired document",
  fraudulentDocument: "Fraudulent document",
  vehicleMismatch: "Vehicle mismatch",
  other: "Other",
};

interface DocRow {
  docType: DriverDocumentType;
  label: string;
  doc?: DocDetail;
  src?: string;
}

function docRows(docs: Driver["documents"]): DocRow[] {
  return [
    {
      docType: "licenseDetails",
      label: "Driver licence",
      doc: docs?.licenseDetails,
      src: docs?.licenseDetails?.frontImageUrl,
    },
    {
      docType: "latraVehicleLicenseD4Details",
      label: "LATRA D4 permit",
      doc: docs?.latraVehicleLicenseD4Details,
      src: docs?.latraVehicleLicenseD4Details?.imageUrl,
    },
    {
      docType: "vehicleInsuranceDetails",
      label: "Vehicle insurance",
      doc: docs?.vehicleInsuranceDetails,
      src: docs?.vehicleInsuranceDetails?.imageUrl,
    },
  ];
}

/**
 * One-stop approval. The backend will not approve a driver until every
 * document is approved, so instead of bouncing the admin to the Documents tab
 * and back, the documents are previewed and approved/rejected right here, and
 * the driver is activated from the same place.
 *
 * It is also where the licence expiry is captured: drivers stopped entering it,
 * so an admin reads it off the licence scan while checking the document. The
 * same goes for the LATRA details (vehicle registration date, plate, licence
 * number): the backend refuses approval until they are in a shape CeTS accepts.
 */
export function ApproveDriverModal({
  opened,
  onClose,
  driverName,
  documents,
  rideTypes,
  loading,
  reviewing,
  storedLicenseExpiry,
  storedLatra,
  onApproveDocument,
  onRejectDocument,
  onConfirm,
}: {
  opened: boolean;
  onClose: () => void;
  driverName?: string;
  documents: Driver["documents"];
  rideTypes: RideType[];
  /** Driver approval in flight. */
  loading: boolean;
  /** A document review in flight. */
  reviewing: boolean;
  /** Existing expiry, if one was captured during document review. */
  storedLicenseExpiry?: string;
  /** LATRA details already on the driver record, used to prefill. */
  storedLatra: {
    vehicleRegistrationDate?: string;
    registrationNumber?: string;
    licenseNumber?: string;
  };
  onApproveDocument: (
    docType: DriverDocumentType,
    licenseExpiryDate?: string,
  ) => void;
  onRejectDocument: (docType: DriverDocumentType, label: string) => void;
  onConfirm: (
    vehicleCategoryID: string,
    reason: string | undefined,
    licenseExpiryDate: string,
    latra: LatraApprovalDetails,
  ) => void;
}) {
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState<Date | null>(null);
  const [plate, setPlate] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  // storedLatra is a fresh object each render, so key the effect on its fields.
  const {
    registrationNumber: storedPlate,
    licenseNumber: storedLicenseNumber,
  } = storedLatra;
  const [actingOn, setActingOn] = useState<DriverDocumentType | null>(null);

  // Re-seed each time the modal opens, so reopening after a failed approval
  // does not show a stale date from the previous attempt.
  useEffect(() => {
    if (!opened) return;
    setLicenseExpiry(fromDdMmYyyy(storedLicenseExpiry));
    setPlate(storedPlate ?? "");
    setLicenseNumber(storedLicenseNumber ?? "");
  }, [
    opened,
    storedLicenseExpiry,
    storedPlate,
    storedLicenseNumber,
  ]);

  const rows = docRows(documents);
  const approvedCount = rows.filter(
    (r) => r.src && r.doc?.status === "approved",
  ).length;
  const missing = rows.filter((r) => !r.src);
  const allApproved = approvedCount === rows.length;

  const options = rideTypes.map((rt) => ({
    value: rt._id,
    // Only append the vehicle type when present, otherwise the label rendered a
    // bare "(—)" which looked like a broken/placeholder option.
    label: rt.vehicleType
      ? `${en(rt.name)} (${humanize(rt.vehicleType)})`
      : en(rt.name),
  }));

  const expiryDate = toDdMmYyyy(licenseExpiry);
  const plateErr = plateError(plate);
  const licenseErr = licenseError(licenseNumber);
  const latraReady = Boolean(
    plate.trim() &&
      !plateErr &&
      licenseNumber.trim() &&
      !licenseErr,
  );
  const canSubmit = Boolean(
    allApproved && categoryId && expiryDate && latraReady,
  );

  const blocker = !allApproved
    ? "Approve all documents first"
    : !categoryId
      ? "Pick a vehicle category"
      : !expiryDate
        ? "Enter the licence expiry"
        : !latraReady
          ? "Complete the LATRA details"
          : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={driverName ? `Review & approve — ${driverName}` : "Review & approve"}
      size="xl"
      centered
    >
      <Stack gap="md">
        <Box>
          <Group justify="space-between" mb={6}>
            <Text size="sm" fw={600}>
              Documents
            </Text>
            <Text size="xs" c="dimmed">
              {approvedCount} of {rows.length} approved
            </Text>
          </Group>
          <Progress
            value={(approvedCount / rows.length) * 100}
            color="green"
            size="sm"
            radius="xl"
          />
        </Box>

        {!allApproved && (
          <Alert
            variant="light"
            color="yellow"
            icon={<IconAlertTriangle size={16} />}
            p="xs"
          >
            <Text size="xs">
              {missing.length > 0
                ? `Not submitted yet: ${missing.map((m) => m.label).join(", ")}. The driver has to upload ${missing.length > 1 ? "them" : "it"} before approval.`
                : "Check each document below and approve it. The driver can be activated once all three are approved."}
            </Text>
          </Alert>
        )}

        <Stack gap="xs">
          {rows.map((r) => {
            const status = r.doc?.status ?? "pending";
            const submitted = Boolean(r.src);
            const isLicence = r.docType === "licenseDetails";
            const needsExpiry = isLicence && !r.doc?.expiryDate;
            const busy = reviewing && actingOn === r.docType;
            return (
              <Paper key={r.docType} withBorder radius="md" p="sm">
                <Group align="flex-start" wrap="nowrap" gap="md">
                  <Box w={190} style={{ flexShrink: 0 }}>
                    <ImageThumb src={r.src} label={r.label} h={130} />
                  </Box>

                  <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Group justify="space-between" wrap="nowrap">
                      <Text fw={600} size="sm">
                        {r.label}
                      </Text>
                      <Badge
                        size="sm"
                        variant="light"
                        color={submitted ? DOC_STATUS_COLORS[status] : "gray"}
                        leftSection={
                          status === "approved" ? (
                            <IconCircleCheck size={12} />
                          ) : undefined
                        }
                      >
                        {submitted ? humanize(status) : "Not submitted"}
                      </Badge>
                    </Group>

                    {(r.doc?.licenseNumber || r.doc?.expiryDate) && (
                      <Text size="xs" c="dimmed">
                        {r.doc?.licenseNumber ? `No: ${r.doc.licenseNumber}` : ""}
                        {r.doc?.licenseNumber && r.doc?.expiryDate ? " · " : ""}
                        {r.doc?.expiryDate ? `Exp: ${r.doc.expiryDate}` : ""}
                      </Text>
                    )}

                    {status === "rejected" && r.doc?.rejectionReason && (
                      <Text size="xs" c="var(--error-fg)">
                        Rejected: {REJECTION_LABELS[r.doc.rejectionReason]}
                        {r.doc.rejectionComment
                          ? ` — ${r.doc.rejectionComment}`
                          : ""}
                      </Text>
                    )}

                    {needsExpiry && submitted && (
                      <DateInput
                        size="xs"
                        label="Licence expiry"
                        description="Read it off the licence image"
                        placeholder="Pick the expiry date"
                        value={licenseExpiry}
                        onChange={(d) =>
                          setLicenseExpiry(
                            parseBackendDate(d as Date | string | null),
                          )
                        }
                        minDate={new Date()}
                        required
                      />
                    )}

                    {submitted && (
                      <Group gap="xs" mt={2}>
                        <Button
                          size="xs"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                          loading={busy}
                          disabled={
                            status === "approved" ||
                            (reviewing && !busy) ||
                            (needsExpiry && !expiryDate)
                          }
                          onClick={() => {
                            setActingOn(r.docType);
                            onApproveDocument(
                              r.docType,
                              isLicence ? expiryDate : undefined,
                            );
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconX size={14} />}
                          disabled={reviewing || status === "rejected"}
                          onClick={() => onRejectDocument(r.docType, r.label)}
                        >
                          Reject
                        </Button>
                      </Group>
                    )}
                  </Stack>
                </Group>
              </Paper>
            );
          })}
        </Stack>

        <Stack gap="xs" opacity={allApproved ? 1 : 0.5}>
          <Text size="sm" fw={600}>
            Activate driver
          </Text>
          <Select
            label="Vehicle category"
            description="Assigning a category makes the driver eligible for matching"
            placeholder="Select a ride type"
            data={options}
            value={categoryId}
            onChange={setCategoryId}
            required
            searchable
            disabled={!allApproved}
            nothingFoundMessage="No active ride types"
          />
          <Text size="xs" c="dimmed" mt={4}>
            LATRA details: check them against the registration card and
            licence. LATRA rejects the driver and all their trips otherwise.
            The vehicle is registered with LATRA as of today's approval.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Plate number"
              placeholder="T123ABC"
              value={plate}
              onChange={(e) => setPlate(e.currentTarget.value)}
              error={plateErr}
              disabled={!allApproved}
              required
            />
            <TextInput
              label="Licence number"
              placeholder="10 digits"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.currentTarget.value)}
              error={licenseErr}
              disabled={!allApproved}
              required
            />
          </SimpleGrid>
          <Textarea
            label="Reason (optional)"
            placeholder="All documents verified"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={!allApproved}
            autosize
            minRows={2}
          />
        </Stack>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip label={blocker} disabled={!blocker} withArrow>
            <Box>
              <Button
                color="green"
                loading={loading}
                disabled={!canSubmit}
                onClick={() =>
                  categoryId &&
                  expiryDate &&
                  onConfirm(categoryId, reason || undefined, expiryDate, {
                    registrationNumber: normalizePlate(plate),
                    licenseNumber: normalizeLicense(licenseNumber),
                  })
                }
              >
                Approve driver
              </Button>
            </Box>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  );
}
